import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { languages } from '@/i18n/config';

interface LanguageSwitcherProps {
  variant?: 'icon' | 'full' | 'minimal';
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'icon',
  className = ''
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={variant === 'icon' ? 'icon' : 'default'}
          className={`flex items-center gap-2 ${className}`}
        >
          {variant === 'icon' ? (
            <Globe className="h-5 w-5" />
          ) : variant === 'minimal' ? (
            <>
              <span className="text-lg">{currentLanguage.flag}</span>
              <span className="text-sm font-medium">{currentLanguage.code.toUpperCase()}</span>
            </>
          ) : (
            <>
              <Globe className="h-4 w-4" />
              <span className="text-lg">{currentLanguage.flag}</span>
              <span className="text-sm">{currentLanguage.name}</span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[150px]">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={`flex items-center gap-3 cursor-pointer ${
              i18n.language === language.code ? 'bg-blue-50 text-blue-700' : ''
            }`}
          >
            <span className="text-xl">{language.flag}</span>
            <span className="flex-grow">{language.name}</span>
            {i18n.language === language.code && (
              <span className="text-blue-500 text-sm">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
